import React, { useState } from 'react';
import { auth } from '../../firebase-config';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import {
  Button,
  Card,
  EyeOffIcon,
  EyeOpenIcon,
  Heading,
  Link,
  Text,
  TextInputField,
  Tooltip,
  Position,
  Checkbox,
  Icon,
  Pane,
  LockIcon,
  PersonIcon,
} from 'evergreen-ui';
import { toastNotification } from '../../shared/toastNotification';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginValuesActions } from '../../store/loginValues';
import { useForm } from 'react-hook-form';
import { checkString } from '../../helpers/signInUpValidation';
import { getVitaUserByIdentityId } from '../../services/api/vitaUserService';
import { toast } from 'react-toastify';
import { backendUrl } from '../../config/config';

export default function SignInComponent() {
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    formState,
    register,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const {
    errors,
    touchedFields,
  } = formState;

  const fetchLogin = (signInObject) => {
    setLoading(true);
    let statusResponse = false;
    let toastContent = '';
    signInObject = {
      email: signInObject.email.toLowerCase().trim(),
      password: signInObject.password,
    };

    signInWithEmailAndPassword(auth, signInObject.email, signInObject.password)
      .then((userCredential) => {
        getVitaUserByIdentityId(userCredential.user.uid)
          .then((userResponse) => {
            if (!userResponse.data.customerId) {
              navigate('/empresas');
              return;
            }
            fetch(`${backendUrl}/subscriptions/check`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                user: userResponse.data,
              }),
            })
              .then((response) => {
                if (response.status === 200) {
                  return response.json();
                } else {
                  return response.text().then((text) => {
                    throw new Error(text);
                  });
                }
              })
              .then(() => {
                statusResponse = 'success';
                toastContent = 'Logado com sucesso';
                setLoading(false);
                toastNotification(statusResponse, toastContent);
                navigate('/empresas');
              })
              .catch((error) => {
                signOut(auth);
                console.log('Subscription error: ', error.message);
                toast.error(error.message);
                return;
              });
          })
          .catch((error) => {
            console.log('catch error: ', error);
            if (error === undefined)
              toastContent =
                'Parece que falhamos aqui. Por favor, tente novamente.';
            else toastContent = error.data.errors.Mensagens[0];

            statusResponse = 'error';
          });
      })
      .catch((error) => {
        if (error.code === 'auth/user-not-found') {
          statusResponse = 'error';
          toastContent = 'Usuário não encontrado.';
        } else if (error.code === 'auth/invalid-credential') {
          statusResponse = 'error';
          toastContent = 'Senha incorreta.';
        } else if (error.code === 'auth/too-many-requests') {
          statusResponse = 'error';
          toastContent = 'Muitas tentativas. Tente novamente mais tarde.';
        } else {
          statusResponse = 'error';
          toastContent = 'Parece que falhamos aqui. Por favor, tente novamente.';
        }
        toastNotification(statusResponse, toastContent);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Card
      className="login-form-card"
      background='white'
      padding={24}
      display='flex'
      flexDirection='column'
      borderTop="3px solid #27E2E7"
    >
      <Heading size={700} marginBottom={24} textAlign="center" color="#333">
        Login
      </Heading>
      
      <form
        id='signup-form'
        className="login-form"
        onSubmit={handleSubmit(fetchLogin)}
      >
        <div className="form-group">
          <label htmlFor="email" className="input-label">E-mail</label>
          <div className="input-container">
            <div className="input-icon">
              <PersonIcon color="#27E2E7" size={16} />
            </div>
            <input
              {...register(
                'email',
                {
                  pattern: {
                    value: /^[\sa-zA-Z0-9._-]+@[a-zA-Z0-9]{2,}.[a-zA-Z]{2,}(.?[a-z\s]{2,})?$/gm,
                    message: 'Digite um e-mail válido.',
                  },
                  required: 'Email é obrigatório'
                }
              )}
              id="email"
              className="input-field"
              type='email'
              placeholder='seu@email.com'
            />
          </div>
          {errors?.email && <p className="error-message">{errors.email.message}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="password" className="input-label">Senha</label>
          <div className="input-container">
            <div className="input-icon">
              <LockIcon color="#27E2E7" size={16} />
            </div>
            <input
              {...register(
                'password',
                {
                  pattern: {
                    value: /^(?=.*\d)(?=.*[!@#$%&*])(?=.*[a-z])(?=.*[A-Z]).{6,20}$/gm,
                  },
                  minLength: {
                    value: 6,
                  },
                  required: 'Senha é obrigatória'
                }
              )}
              id="password"
              className="input-field"
              type={showPassword ? 'text' : 'password'}
              placeholder='••••••••'
            />
            <button 
              type="button"
              onClick={() => setShowPassword(!showPassword)} 
              className="password-toggle"
              tabIndex="-1"
            >
              {showPassword ? (
                <EyeOpenIcon color='#27E2E7' size={16} />
              ) : (
                <EyeOffIcon color='#7e8490' size={16} />
              )}
            </button>
          </div>
          {errors?.password && <p className="error-message">{errors.password.message}</p>}
        </div>

        <div className="password-validation">
          {touchedFields.password && checkString(watch('password'))}
        </div>

        <div className="login-options">
          <div className="remember-me">
            <input 
              type="checkbox" 
              id="remember" 
              className="remember-checkbox" 
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <label htmlFor="remember" className="remember-label">Manter conectado</label>
          </div>
          <Link href='/alterar-senha' className="forgot-password">
            Esqueci minha senha
          </Link>
        </div>
        
        <Button
          className="login-button"
          size='large'
          height={44}
          marginTop={24}
          marginBottom={8}
          isLoading={loading}
          appearance='primary'
          width="100%"
          intent="success"
          type='submit'
        >
          Entrar
        </Button>
      </form>

      <style jsx='true'>
      {`
        .login-form-card {
          width: 100%;
          transition: all 0.3s ease;
        }
        
        .login-form {
          width: 100%;
        }
        
        .form-group {
          margin-bottom: 16px;
          position: relative;
        }
        
        .input-label {
          display: block;
          margin-bottom: 6px;
          font-size: 14px;
          font-weight: 500;
          color: #425A70;
        }
        
        .input-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .input-icon {
          position: absolute;
          left: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }
        
        .input-field {
          width: 100%;
          height: 40px;
          padding: 8px 12px 8px 36px;
          font-size: 14px;
          border: 1px solid #d8dae5;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        
        .input-field:focus {
          outline: none;
          border-color: #27E2E7;
          box-shadow: 0 0 0 2px rgba(39, 226, 231, 0.2);
        }
        
        .password-toggle {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }
        
        .error-message {
          color: #D14343;
          font-size: 12px;
          margin-top: 4px;
          margin-bottom: 0;
        }
        
        .password-validation {
          margin-top: 8px;
          margin-bottom: 16px;
        }
        
        .login-options {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .remember-me {
          display: flex;
          align-items: center;
        }
        
        .remember-checkbox {
          margin-right: 8px;
          cursor: pointer;
        }
        
        .remember-label {
          font-size: 14px;
          color: #66788A;
          cursor: pointer;
        }
        
        .forgot-password {
          color: #27E2E7;
          text-decoration: none;
          font-size: 14px;
          transition: color 0.2s ease;
        }
        
        .forgot-password:hover {
          color: #1fa8ac;
          text-decoration: underline;
        }
        
        .login-button {
          background: linear-gradient(135deg, #27E2E7 0%, #53BCC3 100%) !important;
          transition: transform 0.3s ease, box-shadow 0.3s ease !important;
          border: none !important;
          box-shadow: 0 4px 6px rgba(39, 226, 231, 0.2) !important;
          font-weight: 600 !important;
        }
        
        .login-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(39, 226, 231, 0.3) !important;
          background: linear-gradient(135deg, #1fa8ac 0%, #3a8a8f 100%) !important;
        }
        
        .login-button:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(39, 226, 231, 0.2) !important;
        }
        
        @media (max-width: 480px) {
          .login-options {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          
          .login-form-card {
            padding: 20px 16px;
          }
        }
      `}
      </style>
    </Card>
  );
}
